import { useIntl, useUnits } from '@bbr/intl';
import { createMemo, splitProps } from 'solid-js';
import type { UserUnits } from '@bbr/api-client';
import type { FormatNumberOptions } from '@formatjs/intl';
import type { Accessor } from 'solid-js';

export function convertDistance(dist: number, units: UserUnits = 'imperial', preferMiles: boolean = false) {
	if (units === 'imperial') {
		const feet = Math.round(dist * 328.084) / 100;
		if (preferMiles) {
			return feet / 5280;
		}
		return feet;
	}
	return dist;
}

type Props = {
	distance: number;
	preferMiles?: boolean;
} & FormatNumberOptions;

export function Distance(props: Props) {
	const intl = useIntl();
	const [units] = useUnits();
	const [, rest] = splitProps(props, ['distance', 'preferMiles']);
	const formatOptions = createLengthFormatOptions(() => ({
		unit: units() === 'imperial' ? (props.preferMiles ? 'mile' : 'foot') : 'meter',
		maximumFractionDigits: units() === 'imperial' && props.preferMiles ? 1 : 0,
		minimumFractionDigits: units() === 'imperial' && props.preferMiles ? 1 : 0,
		...rest,
	}));

	return <>{intl().formatNumber(convertDistance(props.distance, units(), props.preferMiles), formatOptions())}</>;
}

export function createLengthFormatOptions(overrides: Accessor<FormatNumberOptions> = () => ({})) {
	const [units] = useUnits();
	const out = createMemo((): FormatNumberOptions => ({
		style: 'unit',
		unit: units() === 'metric' ? 'meter' : 'foot',
		unitDisplay: units() === 'metric' ? 'narrow' : 'short',
		notation: 'compact',
		maximumFractionDigits: 0,
		...overrides(),
	}));
	return out;
}
